import {useCallback} from 'react';

import {useCommonDataHook} from 'src/hooks/use-common-data-hook';
import {getUserSubscriptions, updateUserSubscriptions} from 'src/openapi/admin/apis/UserLcdSubscriptionsApi';
import {useAppDispatch} from 'src/store/hooks';

import {LcdSubscriptions} from '../typedef';

import type {SaveOptions} from 'src/components/save-with-reason/save-with-reason-button';
import type {RegionSettings} from 'src/openapi/admin/models/RegionSettings';
import type {SuccessPayload} from 'src/typedef';

type Props = {
    userId: number,
};

const DEFAULT_STATE: Record<string, RegionSettings[]> = {
    [LcdSubscriptions.NEWS]: [],
    [LcdSubscriptions.RESEARCH]: [],
};

export const useLcdSubscriptions = ({userId}: Props) => {
    const apiDispatch = useAppDispatch();

    const getDispatchAction = useCallback(async (signal: AbortSignal) => {
        const response = await apiDispatch(getUserSubscriptions({userId}, {signal}));
        if (response.payload.success) {
            const subscriptions = response.payload.data;
            const isExisting = Object.keys(subscriptions).length;

            if (isExisting) {
                return Promise.resolve({type: '', payload: response.payload});
            } else {
                return Promise.resolve({type: '', payload: {
                    success: true,
                    error: false,
                    data: DEFAULT_STATE,
                } as SuccessPayload<Record<string, RegionSettings[]>>});
            }
        } else {
            return Promise.resolve({type: '', payload: response.payload});
        }
    }, [apiDispatch, userId]);

    const saveDispatchAction = useCallback((
        requestBody: Record<string, RegionSettings[]>,
        signal: AbortSignal,
        options?: SaveOptions,
    ) => (
        apiDispatch(updateUserSubscriptions({
            userId,
            updateUserLcdSubscriptionsRequest: {subscriptions: requestBody, reason: options?.reason},
        }, {signal}))
    ), [apiDispatch, userId]);

    return useCommonDataHook({
        initValue: DEFAULT_STATE,
        getDispatchAction,
        saveDispatchAction,
    });
};
